/**
 * Same-origin cross-tab session handoff over BroadcastChannel. A booting tab
 * with no stored session asks; any live signed-in tab answers with its
 * serialized session (tokens move between same-origin tabs only — nothing is
 * persisted beyond each tab's sessionStorage). A link from another app lands
 * instantly instead of re-walking SSO whenever the dashboard is open anywhere.
 */

const CHANNEL = 'wb-auth';

let donorChannel: BroadcastChannel | null = null;

interface BridgeAsk {
  type: 'who-has-session';
  nonce: string;
}

interface BridgeAnswer {
  type: 'session';
  replyTo: string;
  user: string;
}

/** Start answering other tabs' session requests (idempotent). */
export function initSessionBridge(getSerializedUser: () => string | null): void {
  if (donorChannel || typeof BroadcastChannel === 'undefined') return;
  try {
    donorChannel = new BroadcastChannel(CHANNEL);
    donorChannel.onmessage = (ev: MessageEvent<BridgeAsk>) => {
      if (ev.data?.type !== 'who-has-session') return;
      const user = getSerializedUser();
      if (user) {
        const answer: BridgeAnswer = { type: 'session', replyTo: ev.data.nonce, user };
        donorChannel?.postMessage(answer);
      }
    };
  } catch {
    donorChannel = null;
  }
}

/** Ask live tabs for a session; null after `timeoutMs` of silence. */
export function requestSession(timeoutMs = 300): Promise<string | null> {
  if (typeof BroadcastChannel === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    let ch: BroadcastChannel;
    try {
      ch = new BroadcastChannel(CHANNEL);
    } catch {
      resolve(null);
      return;
    }
    const nonce = crypto.getRandomValues(new Uint32Array(2)).join('-');
    let settled = false;
    const finish = (v: string | null): void => {
      if (settled) return;
      settled = true;
      ch.close();
      resolve(v);
    };
    ch.onmessage = (ev: MessageEvent<BridgeAnswer>) => {
      if (
        ev.data?.type === 'session' &&
        ev.data.replyTo === nonce &&
        typeof ev.data.user === 'string'
      ) {
        finish(ev.data.user);
      }
    };
    const ask: BridgeAsk = { type: 'who-has-session', nonce };
    ch.postMessage(ask);
    window.setTimeout(() => finish(null), timeoutMs);
  });
}
